export type ChatRunOutcome = "running" | "success" | "error";

interface ChatRunRecord {
  chatId: string;
  runId: number;
  outcome: ChatRunOutcome;
  // Worst unread terminal outcome for this chat; null once the user has seen it.
  // One record per chatId means (n) counts chats, not runs.
  unread: "error" | "success" | null;
  // Client epoch ms when this run started; drives the live elapsed timer so it
  // survives the chat view unmounting (navigate away and back).
  startedAt: number;
}

type Listener = () => void;

const records = new Map<string, ChatRunRecord>();
const listeners = new Set<Listener>();

let displayedChatId: string | null = null;
let tabVisible = typeof document === "undefined" || document.visibilityState !== "hidden";
let nextRunId = 1;
let summary = "idle|0";

function worstUnread(
  a: "error" | "success" | null,
  b: "error" | "success" | null,
): "error" | "success" | null {
  if (a === "error" || b === "error") return "error";
  if (a === "success" || b === "success") return "success";
  return null;
}

function calculateSummary(): string {
  let errors = 0;
  let successes = 0;
  let running = 0;

  records.forEach((record) => {
    if (record.outcome === "running") running += 1;
    if (record.unread === "error") errors += 1;
    else if (record.unread === "success") successes += 1;
  });

  if (errors > 0) return `error|${errors}`;
  if (successes > 0) return `done|${successes}`;
  if (running > 0) return "working|0";
  return "idle|0";
}

function publishIfChanged(): void {
  const nextSummary = calculateSummary();
  if (nextSummary === summary) return;
  summary = nextSummary;
  listeners.forEach((listener) => listener());
}

function acknowledgeSeen(): void {
  const documentVisible =
    typeof document === "undefined" || document.visibilityState !== "hidden";
  if (!tabVisible || !documentVisible || !displayedChatId) return;
  const record = records.get(displayedChatId);
  if (!record) return;
  if (record.outcome === "running") {
    if (record.unread === null) return;
    records.set(displayedChatId, { ...record, unread: null });
  } else {
    records.delete(displayedChatId);
  }
}

export function startRun(chatId: string): number {
  const runId = nextRunId;
  nextRunId += 1;
  const existing = records.get(chatId);
  records.set(chatId, {
    chatId,
    runId,
    outcome: "running",
    unread: existing?.unread ?? null,
    startedAt: Date.now(),
  });
  publishIfChanged();
  return runId;
}

export function finishRun(
  chatId: string,
  runId: number,
  outcome: Exclude<ChatRunOutcome, "running">,
): void {
  const record = records.get(chatId);
  if (!record || record.runId !== runId) return;
  records.set(chatId, { ...record, outcome, unread: worstUnread(record.unread, outcome) });
  acknowledgeSeen();
  publishIfChanged();
}

export function dropRun(chatId: string, runId: number): void {
  const record = records.get(chatId);
  if (!record || record.runId !== runId) return;
  if (record.unread) {
    records.set(chatId, { ...record, outcome: record.unread });
  } else {
    records.delete(chatId);
  }
  publishIfChanged();
}

export function setDisplayedChatId(chatId: string | null): void {
  displayedChatId = chatId;
  acknowledgeSeen();
  publishIfChanged();
}

export function setTabVisible(visible: boolean): void {
  tabVisible = visible;
  acknowledgeSeen();
  publishIfChanged();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Start time (epoch ms) of the chat's in-flight run, or null when none is running. */
export function getRunningStartedAt(chatId: string): number | null {
  const record = records.get(chatId);
  return record?.outcome === "running" ? record.startedAt : null;
}

export function getSummary(): string {
  return summary;
}

export function resetChatRunTracker(): void {
  records.clear();
  displayedChatId = null;
  tabVisible = typeof document === "undefined" || document.visibilityState !== "hidden";
  nextRunId = 1;
  const previousSummary = summary;
  summary = "idle|0";
  if (previousSummary !== summary) {
    listeners.forEach((listener) => listener());
  }
}
